import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** px/second for the scroll */
  speed?: number;
  /** wait N ms at each end before flipping direction */
  pauseMs?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Auto-scrolls its content back-and-forth ONLY when it overflows the container.
 * Fits short strings statically; long ones drift left, pause, drift right,
 * pause, repeat — same behavior as Spotify iOS / Apple Music's Now Playing.
 *
 * ResizeObserver keeps the overflow measurement live: rotate the device or
 * resize the container and we re-measure without a remount.
 */
export function Marquee({ children, speed = 30, pauseMs = 1200, className, style }: Props) {
  const outer = useRef<HTMLDivElement | null>(null);
  const inner = useRef<HTMLSpanElement | null>(null);
  const [overflow, setOverflow] = useState(0);

  useLayoutEffect(() => {
    const el = outer.current, span = inner.current;
    if (!el || !span) return;
    const measure = () => {
      const diff = span.scrollWidth - el.clientWidth;
      setOverflow(diff > 1 ? diff : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el); ro.observe(span);
    return () => ro.disconnect();
  }, [children]);

  useEffect(() => {
    if (overflow === 0) return;
    const dist = overflow;
    const travelMs = (dist / speed) * 1000;
    const el = inner.current; if (!el) return;
    let cancelled = false;

    const go = async (offset: number) => {
      if (cancelled) return;
      el.style.transition = `transform ${travelMs}ms linear`;
      el.style.transform = `translateX(${-offset}px)`;
    };

    let step = 0;
    const tick = async () => {
      if (cancelled) return;
      if (step % 2 === 0) {
        await go(dist);              // drift left
      } else {
        await go(0);                 // drift back to origin
      }
      step += 1;
      setTimeout(tick, travelMs + pauseMs);
    };

    // start with a brief hold so the user reads the start before it moves
    setTimeout(tick, pauseMs);
    return () => {
      cancelled = true;
      if (el) {
        el.style.transition = "none";
        el.style.transform = "translateX(0)";
      }
    };
  }, [overflow, speed, pauseMs]);

  return (
    <div
      ref={outer}
      className={className}
      style={{
        overflow: "hidden",
        whiteSpace: "nowrap",
        maskImage: overflow > 0
          ? "linear-gradient(90deg, transparent 0, #000 8px, #000 calc(100% - 12px), transparent 100%)"
          : undefined,
        WebkitMaskImage: overflow > 0
          ? "linear-gradient(90deg, transparent 0, #000 8px, #000 calc(100% - 12px), transparent 100%)"
          : undefined,
        ...style,
      }}
    >
      <span
        ref={inner}
        style={{
          display: "inline-block",
          willChange: overflow > 0 ? "transform" : undefined,
        }}
      >
        {children}
      </span>
    </div>
  );
}
